import { Link } from "wouter";
import { motion } from "framer-motion";
import { Bot, Database, Plug, Webhook, Brain, Sparkles, Network, Mail, Cpu, MessageSquare, BarChart3, Workflow, CheckCircle, Zap, DollarSign, FileText, Users, RefreshCw, Search, UserCog, ShieldCheck, Rocket } from "lucide-react";
import agentsToGoLogo from "@assets/AGENTS-02_1_1778254256444.webp";
import { Seo, breadcrumbSchema } from "@/lib/seo";

type ToolItem = { name: string; logo?: string; Icon?: React.ElementType };

const agentExamples = [
  { title: "Customer Support Agent", description: "Answers FAQs, drafts responses, routes tickets, and escalates complex issues to your team.", badge: "Support", Icon: MessageSquare },
  { title: "Lead Qualification Agent", description: "Reviews inbound leads, enriches records, scores opportunities, and triggers sales follow-ups.", badge: "Sales", Icon: Users },
  { title: "Invoice Processing Agent", description: "Extracts invoice details, validates fields, flags exceptions, and updates finance systems.", badge: "Finance", Icon: FileText },
  { title: "Internal Knowledge Agent", description: "Searches company docs, answers employee questions, and keeps internal teams moving faster.", badge: "Ops", Icon: Database },
  { title: "CRM Update Agent", description: "Summarizes calls, updates customer records, creates tasks, and keeps pipeline data clean.", badge: "CRM", Icon: RefreshCw },
  { title: "Workflow Automation Agent", description: "Connects tools, monitors triggers, performs repetitive tasks, and logs every action.", badge: "Automation", Icon: Workflow },
];

const aboutFeatures = [
  { title: "Fully Implemented Solutions", description: "We configure, integrate, test, and deploy AI agents directly into your workflows.", Icon: CheckCircle },
  { title: "Business-Ready Workflows", description: "Every implementation is designed for operational efficiency and measurable ROI.", Icon: Zap },
  { title: "Fixed-Price Builds", description: "Clear deliverables, transparent pricing, and predictable timelines.", Icon: DollarSign },
  { title: "Stack Integration", description: "Connect AI agents with your APIs, CRMs, databases, and internal systems.", Icon: Plug },
];

const credibilityItems: { label: string; Icon: React.ElementType; tools: ToolItem[] }[] = [
  {
    label: "AI Models",
    Icon: Cpu,
    tools: [
      { name: "OpenAI",  Icon: Cpu },
      { name: "Claude",  Icon: Brain },
      { name: "Grok",    Icon: Sparkles },
    ],
  },
  {
    label: "Agent Frameworks",
    Icon: Bot,
    tools: [
      { name: "LangChain",     Icon: Network },
      { name: "Custom Agents", Icon: Bot },
      { name: "RAG Systems",   Icon: Database },
    ],
  },
  {
    label: "Automation",
    Icon: Workflow,
    tools: [
      { name: "n8n",      Icon: Workflow },
      { name: "APIs",     Icon: Plug },
      { name: "Webhooks", Icon: Webhook },
    ],
  },
  {
    label: "Business Tools",
    Icon: BarChart3,
    tools: [
      { name: "Slack",            Icon: MessageSquare },
      { name: "HubSpot",          Icon: BarChart3 },
      { name: "Google Workspace", Icon: Mail },
    ],
  },
];

const proofStats = [
  ["Fixed-price", "implementation scopes"],
  ["Human-in-the-loop", "review options"],
  ["End-to-end", "deployment support"],
];

const steps = [
  { number: "01", Icon: Search,     title: "Discover Your Use Case", description: "We identify repetitive workflows, bottlenecks, and operational tasks that can benefit from AI automation." },
  { number: "02", Icon: Bot,        title: "Choose an AI Agent",     description: "Browse our curated catalog or let our team recommend the best-fit AI solution for your business." },
  { number: "03", Icon: UserCog,    title: "Customize & Integrate",  description: "We tailor the AI agent to your workflows and integrate it into your existing stack." },
  { number: "04", Icon: ShieldCheck,title: "Deploy & Test",          description: "We validate workflows, optimize outputs, and ensure the system performs reliably before launch." },
  { number: "05", Icon: Rocket,     title: "Launch & Scale",         description: "Expand automation across departments and scale operations with confidence." },
];

const faqs = [
  { question: "Do I need a technical team?", answer: "No. We handle implementation, integrations, deployment, and setup for you." },
  { question: "How long does implementation take?", answer: "Most AI agent deployments can be completed within days to weeks depending on complexity." },
  { question: "Can the agents integrate with our tools?", answer: "Yes. We support APIs, CRMs, databases, automation systems, and internal business tools." },
  { question: "Are humans still involved?", answer: "Absolutely. Human review, approvals, and escalation layers can be added wherever needed." },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.45, delay },
});

export default function About() {
  return (
    <div className="min-h-screen scifi-wave-bg flex flex-col text-white">
      <Seo
        title="How It Works | AgentsToGo Custom AI Agent Implementation Process"
        description="How AgentsToGo works: tell us your workflow, we design and build a custom AI agent, integrate it with your tools, test it, and deploy it for you — typically live in 5–10 business days, with you owning the result."
        path="/how-it-works"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "How It Works", path: "/how-it-works" },
        ])}
      />

      {/* ── NAV ── */}
      <div className="sticky top-0 z-[200] px-4 sm:px-6 py-3" style={{ background: "rgba(8,6,18,0.72)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(192,38,211,0.15)" }}>
        <nav
          className="flex justify-between items-center px-5 sm:px-6 py-2.5 w-full max-w-6xl mx-auto rounded-full"
          style={{ backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", background: "rgba(8,6,18,0.65)", border: "1px solid rgba(192,38,211,0.22)", boxShadow: "0 0 0 1px rgba(0,229,255,0.05), 0 8px 32px rgba(0,0,0,0.35)" }}
        >
          <Link href="/" className="flex items-center gap-2.5 hover:opacity-75 transition-opacity">
            <div className="flex flex-row items-center gap-2">
              <img src={agentsToGoLogo} alt="AgentsToGo" className="h-6 sm:h-7 w-auto object-contain" />
              <p className="text-[0.75rem] font-semibold tracking-tight leading-none" style={{ fontFamily: "'Hanken Grotesk', sans-serif", color: "#cbd5e1" }}>
                The w<span style={{ color: "#22d3ee", fontWeight: 700 }}>AI</span> to go
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-6 text-sm font-medium">
            <Link href="/how-it-works" className="hidden sm:block text-cyan-300 transition-colors">How it Works</Link>
            <Link href="/about" className="hidden text-slate-300 hover:text-cyan-300 transition-colors">About</Link>
            <Link href="/faqs" className="hidden sm:block text-slate-300 hover:text-cyan-300 transition-colors">FAQs</Link>
          </div>
        </nav>
      </div>

      {/* ── HERO ── */}
      <section className="relative overflow-hidden px-6 py-24 lg:px-12 border-b border-cyan-400/10">
        <div className="absolute -top-20 right-1/4 w-96 h-96 bg-fuchsia-500/12 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-6xl text-center">
          <motion.div {...fadeUp(0.05)} className="mb-6 inline-flex rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-300" style={{ border: "1px solid rgba(192,38,211,0.3)", background: "rgba(192,38,211,0.08)" }}>
            Expert-Built AI Agents
          </motion.div>
          <motion.h1 {...fadeUp(0.12)} className="mx-auto max-w-4xl text-4xl sm:text-6xl lg:text-7xl font-bold leading-tight tracking-tight">
            AI agents built for{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-fuchsia-300">
              real business workflows
            </span>
          </motion.h1>
          <motion.p {...fadeUp(0.2)} className="mx-auto mt-7 max-w-3xl text-lg leading-8 text-slate-300">
            AgentsToGo helps businesses deploy expert-built and fully-implemented AI agents customized for operations, support, automation, and internal workflows.
          </motion.p>
          <motion.div {...fadeUp(0.23)} className="mx-auto mt-8 max-w-2xl rounded-2xl px-6 py-5 text-left" style={{ background: "rgba(8,6,18,0.7)", border: "1px solid rgba(0,229,255,0.15)", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
            <h2 className="mb-1.5 text-sm font-semibold text-cyan-300">How does AgentsToGo work?</h2>
            <p className="text-[15px] leading-7 text-slate-300">
              You describe the workflow you want to automate, our team designs and builds a custom AI agent, connects it to your existing tools, tests it, and deploys it for you — typically live within 5–10 business days, with no building required on your side.
            </p>
          </motion.div>
          <motion.div {...fadeUp(0.26)} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link href="/?browse=1" className="rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-8 py-3.5 text-[15px] transition-colors" style={{ boxShadow: "0 0 24px rgba(0,229,255,0.35)" }}>
              Browse Agents
            </Link>
            <Link href="/?book=1" className="rounded-2xl border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 font-semibold px-8 py-3.5 text-[15px] transition-colors">
              Book Consultation
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className="px-6 py-20 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <motion.p {...fadeUp(0)} className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-fuchsia-300">About Us</motion.p>
            <motion.h2 {...fadeUp(0.06)} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              We bridge the gap between AI technology and real-world implementation.
            </motion.h2>
            <motion.p {...fadeUp(0.12)} className="mt-5 text-lg leading-8 text-slate-300">
              Most businesses don't need another AI demo. They need systems that save time, reduce repetitive work, and integrate into existing operations.
            </motion.p>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {aboutFeatures.map((f, i) => (
              <motion.div key={f.title} {...fadeUp(0.05 * i)}
                className="rounded-2xl p-6 transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: "rgba(8,6,18,0.65)", border: "1px solid rgba(0,229,255,0.15)", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.25), rgba(192,38,211,0.25))", border: "1px solid rgba(0,229,255,0.25)" }}>
                  <f.Icon className="w-5 h-5 text-cyan-300" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm leading-6 text-slate-400">{f.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="px-6 py-20 lg:px-12 border-t border-cyan-400/10" style={{ background: "rgba(0,229,255,0.02)" }}>
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-14">
            <motion.p {...fadeUp(0)} className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300">How It Works</motion.p>
            <motion.h2 {...fadeUp(0.06)} className="text-3xl sm:text-5xl font-bold tracking-tight">From idea to deployed AI agent.</motion.h2>
            <motion.p {...fadeUp(0.12)} className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-300">
              Our implementation process is designed to help businesses launch AI workflows quickly, reliably, and without unnecessary complexity.
            </motion.p>
          </div>

          {/* Flow diagram */}
          <motion.div {...fadeUp(0.08)}
            className="rounded-3xl p-6 lg:p-10 mb-14"
            style={{ background: "rgba(8,6,18,0.7)", border: "1px solid rgba(0,229,255,0.15)", boxShadow: "0 0 0 1px rgba(192,38,211,0.06), 0 16px 48px rgba(0,0,0,0.5)" }}
          >
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <div className="mb-4 inline-flex rounded-full px-3 py-1 text-xs font-semibold text-cyan-300 uppercase tracking-widest" style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.2)" }}>
                  Implementation Flow
                </div>
                <h3 className="max-w-xl text-2xl sm:text-3xl font-bold tracking-tight leading-snug">
                  A clear path from business problem to deployed automation.
                </h3>
                <p className="mt-4 max-w-xl text-base leading-7 text-slate-300">
                  Instead of dropping a generic AI tool into your stack, we map the workflow, connect your systems, test the agent, and launch it with the right human controls in place.
                </p>
                <div className="mt-7 grid gap-3 sm:grid-cols-3">
                  {[["Inputs", "Forms, files, tools, requests"], ["Agent Logic", "Reasoning, routing, validation"], ["Outputs", "Actions, updates, reports"]].map(([label, text]) => (
                    <div key={label} className="rounded-xl p-4" style={{ background: "rgba(0,229,255,0.05)", border: "1px solid rgba(0,229,255,0.12)" }}>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300 mb-1">{label}</div>
                      <div className="text-xs leading-5 text-slate-400">{text}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-2xl p-5" style={{ background: "rgba(4,3,12,0.8)", border: "1px solid rgba(0,229,255,0.15)" }}>
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-fuchsia-500/15 blur-2xl" />
                <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-cyan-500/10 blur-2xl" />
                <div className="relative space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[["Trigger", "Customer request"], ["Data", "CRM + docs"]].map(([lbl, val]) => (
                      <div key={lbl} className="rounded-xl p-3.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">{lbl}</div>
                        <div className="mt-1.5 text-sm font-semibold text-white">{val}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center"><div className="h-6 w-px" style={{ background: "rgba(0,229,255,0.3)" }} /></div>
                  <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.15), rgba(192,38,211,0.15))", border: "1px solid rgba(0,229,255,0.25)" }}>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300 mb-2">AI Agent</div>
                    <div className="text-lg font-bold">Analyze, decide, and route</div>
                    <div className="mt-2 text-xs leading-5 text-slate-300">Applies your rules, checks context, and prepares the next action.</div>
                  </div>
                  <div className="flex justify-center"><div className="h-6 w-px" style={{ background: "rgba(0,229,255,0.3)" }} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    {[["Review", "Human approval"], ["Action", "Update systems"]].map(([lbl, val]) => (
                      <div key={lbl} className="rounded-xl p-3.5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500">{lbl}</div>
                        <div className="mt-1.5 text-sm font-semibold text-white">{val}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl p-3 text-center text-xs font-semibold text-cyan-300" style={{ background: "rgba(0,229,255,0.07)", border: "1px solid rgba(0,229,255,0.15)" }}>
                    Every step is tested, logged, and optimized before launch.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 5 steps */}
          <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-5">
            {steps.map((step, i) => (
              <motion.div key={step.number} {...fadeUp(0.04 * i)}
                className="rounded-2xl p-6 flex flex-col items-center text-center transition-all duration-200 hover:-translate-y-1"
                style={{ background: "rgba(8,6,18,0.65)", border: "1px solid rgba(0,229,255,0.15)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
              >
                {/* Icon circle + number badge */}
                <div className="relative mb-5">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: "rgba(0,229,255,0.10)",
                      border: "1px solid rgba(0,229,255,0.35)",
                      boxShadow: "0 0 28px rgba(0,229,255,0.22)",
                    }}
                  >
                    <step.Icon size={26} strokeWidth={2} className="text-cyan-300" />
                  </div>
                  {/* Step number floating badge */}
                  <span
                    className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-slate-950"
                    style={{ background: "linear-gradient(135deg, #22d3ee, #c026d3)", boxShadow: "0 0 10px rgba(0,229,255,0.4)" }}
                  >
                    {step.number}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-white mb-2 leading-snug">{step.title}</h3>
                <p className="text-xs leading-5 text-slate-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROOF / CREDIBILITY ── */}
      <section className="px-6 py-20 lg:px-12 border-t border-cyan-400/10">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <motion.p {...fadeUp(0)} className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-fuchsia-300">Built On Proven Infrastructure</motion.p>
            <motion.h2 {...fadeUp(0.06)} className="text-3xl sm:text-5xl font-bold tracking-tight">Enterprise-grade tools, implemented across your entire stack.</motion.h2>
            <motion.p {...fadeUp(0.12)} className="mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-300">
              AgentsToGo combines modern AI models, agent frameworks, automation platforms, and business integrations to deliver production-ready workflows — not isolated experiments.
            </motion.p>
          </div>

          <div className="mb-5 grid gap-4 sm:grid-cols-3">
            {proofStats.map(([value, label], i) => (
              <motion.div key={value} {...fadeUp(0.04 * i)}
                className="rounded-2xl p-5 text-center"
                style={{ background: "rgba(8,6,18,0.65)", border: "1px solid rgba(0,229,255,0.12)" }}
              >
                <div className="text-xl font-bold text-cyan-300">{value}</div>
                <div className="mt-1.5 text-xs text-slate-400 leading-5">{label}</div>
              </motion.div>
            ))}
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {credibilityItems.map((item, i) => (
              <motion.div key={item.label} {...fadeUp(0.05 * i)}
                className="rounded-2xl p-5 transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: "rgba(8,6,18,0.65)", border: "1px solid rgba(0,229,255,0.13)", boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl text-slate-950" style={{ background: "linear-gradient(135deg, #22d3ee, #c026d3)" }}>
                  <item.Icon className="w-5 h-5 text-slate-950" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-3">{item.label}</h3>
                <div className="flex flex-wrap gap-2">
                  {item.tools.map((tool) => (
                    <span key={tool.name} className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-slate-300" style={{ background: "rgba(0,229,255,0.07)", border: "1px solid rgba(0,229,255,0.15)" }}>
                      {tool.logo && (
                        <img src={tool.logo} alt={tool.name} className="w-3 h-3 object-contain opacity-90" />
                      )}
                      {tool.Icon && (
                        <tool.Icon className="w-3 h-3 text-slate-400" />
                      )}
                      {tool.name}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AGENT EXAMPLES ── */}
      <section className="px-6 py-20 lg:px-12 border-t border-cyan-400/10" style={{ background: "rgba(0,229,255,0.02)" }}>
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <motion.p {...fadeUp(0)} className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300">Agent Catalog</motion.p>
              <motion.h2 {...fadeUp(0.06)} className="max-w-xl text-3xl sm:text-4xl font-bold tracking-tight">
                Start with a proven agent, then customize it for your business.
              </motion.h2>
            </div>
            <motion.p {...fadeUp(0.1)} className="max-w-md text-base leading-7 text-slate-300">
              Choose from common business workflows or let our team design a custom agent around your exact process, tools, and approval rules.
            </motion.p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {agentExamples.map((agent, i) => (
              <motion.div key={agent.title} {...fadeUp(0.04 * i)}
                className="group rounded-2xl p-6 transition-all duration-200 hover:-translate-y-0.5"
                style={{ background: "rgba(8,6,18,0.65)", border: "1px solid rgba(0,229,255,0.13)", boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}
              >
                <div className="mb-5 flex items-center justify-between gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-950" style={{ background: "linear-gradient(135deg, #22d3ee, #c026d3)" }}>
                    <agent.Icon className="w-5 h-5 text-slate-950" />
                  </div>
                  <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-fuchsia-300" style={{ background: "rgba(192,38,211,0.1)", border: "1px solid rgba(192,38,211,0.25)" }}>
                    {agent.badge}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{agent.title}</h3>
                <p className="text-sm leading-6 text-slate-400">{agent.description}</p>
                <Link href="/?browse=1" className="mt-4 inline-block text-xs font-semibold text-cyan-300 hover:text-cyan-200 transition-colors">
                  View workflow →
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="px-6 py-20 lg:px-12 border-t border-cyan-400/10">
        <motion.div {...fadeUp(0.05)}
          className="mx-auto max-w-5xl rounded-3xl p-12 text-center lg:p-16 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.12), rgba(192,38,211,0.12))", border: "1px solid rgba(0,229,255,0.2)", boxShadow: "0 0 60px rgba(0,229,255,0.1), 0 0 60px rgba(192,38,211,0.08)" }}
        >
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,229,255,0.1), transparent)" }} />
          <h2 className="relative text-3xl sm:text-5xl font-bold tracking-tight">Ready to implement AI that actually saves time?</h2>
          <p className="relative mx-auto mt-5 max-w-2xl text-base leading-7 text-slate-300">
            Browse our AI agent catalog or work with our team to build a fully customized implementation for your business.
          </p>
          <div className="relative mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link href="/?browse=1" className="rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-8 py-3.5 text-[15px] transition-colors" style={{ boxShadow: "0 0 24px rgba(0,229,255,0.35)" }}>
              Find My Agent
            </Link>
            <Link href="/?book=1" className="rounded-2xl border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 font-semibold px-8 py-3.5 text-[15px] transition-colors">
              Talk to an Expert
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-6 py-20 lg:px-12 border-t border-cyan-400/10" style={{ background: "rgba(0,0,0,0.2)" }}>
        <div className="mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <motion.p {...fadeUp(0)} className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-fuchsia-300">FAQ</motion.p>
            <motion.h2 {...fadeUp(0.06)} className="text-3xl sm:text-4xl font-bold tracking-tight">Common Questions</motion.h2>
          </div>
          <div className="space-y-4">
            {faqs.slice(0, 2).map((faq, i) => (
              <motion.div key={faq.question} {...fadeUp(0.05 * i)}
                className="rounded-2xl p-6"
                style={{ background: "rgba(8,6,18,0.65)", border: "1px solid rgba(0,229,255,0.12)" }}
              >
                <h3 className="text-base font-semibold text-white mb-2">{faq.question}</h3>
                <p className="text-sm leading-6 text-slate-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp(0.12)} className="mt-6 text-center">
            <Link
              href="/faqs"
              className="inline-flex items-center gap-2 rounded-2xl px-6 py-3 text-sm font-semibold text-cyan-300 hover:text-cyan-200 transition-colors"
              style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.18)" }}
            >
              View all FAQs →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <div className="border-t border-cyan-400/10 px-6 sm:px-12 py-8 text-center" style={{ background: "rgba(0,0,0,0.4)" }}>
        <a href="mailto:bluna@agentstogo.com" className="inline-flex items-center gap-2 text-sm font-medium text-cyan-300 hover:text-cyan-200 hover:underline transition-colors">
          bluna@agentstogo.com
        </a>
        <p className="text-xs text-slate-500 mt-4">AgentsToGo · {new Date().getFullYear()}</p>
      </div>
    </div>
  );
}
